import { type DependencyList, useEffect, useRef } from 'react';

/**
 * Custom hook that triggers an effect only when the dependencies change.
 * Unlike React.useEffect it does not have the initial call of the effect callback.
 *
 * @template T - The type of the dependencies array.
 * @param {function(prev: T, next: T): void} effect - The effect function to be executed when the dependencies change.
 * @param {T} deps - The dependencies array.
 */
export default function useOnChange<T extends DependencyList>(effect: (prev: T, next: T) => void, deps: T) {
    const latestValue = useRef<T>(deps);
    const callback = useRef(effect);
    callback.current = effect;

    useEffect(() => {
        const { current } = latestValue;
        if (current.length !== deps.length || current.some((value, index) => value !== deps[index])) {
            callback.current(latestValue.current, deps);
            latestValue.current = deps;
        }
    }, deps);
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('useOnChange', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
